import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Trophy, ListOrdered, OctagonX } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import api, { money, formatApiError } from "../lib/api";

const STATUS = {
  scheduled: ["Agendado", "text-zinc-400 border-white/15"],
  active: ["Ativo", "text-emerald-400 border-emerald-500/30"],
  ended: ["Encerrado", "text-zinc-500 border-white/10"],
};

const toCents = (v) => {
  const clean = String(v || "").replace(/[^\d.,]/g, "");
  if (!clean) return 0;
  const norm = clean.includes(",") ? clean.replace(/\./g, "").replace(",", ".") : clean;
  const n = parseFloat(norm);
  return isNaN(n) ? 0 : Math.round(n * 100);
};
const fromCents = (c) => ((c || 0) / 100).toFixed(2).replace(".", ",");
const isoToLocal = (iso) => {
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
const fmtDt = (iso) => new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

const BLANK = { product_id: "", variation_id: "", start_at: "", end_at: "", starting_price: "", min_increment: "" };

export default function AdminAuctions() {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [bidsFor, setBidsFor] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/admin/auctions").then((r) => setItems(r.data)).catch(() => {});
    api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openEdit = (a) => setEditing({
    id: a.id, product_id: a.product_id, variation_id: a.variation_id || "",
    start_at: isoToLocal(a.start_at), end_at: isoToLocal(a.end_at),
    starting_price: fromCents(a.starting_price), min_increment: fromCents(a.min_increment),
  });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      product_id: editing.product_id,
      variation_id: editing.variation_id || null,
      start_at: new Date(editing.start_at).toISOString(),
      end_at: new Date(editing.end_at).toISOString(),
      starting_price: toCents(editing.starting_price),
      min_increment: toCents(editing.min_increment),
    };
    try {
      if (editing.id) await api.put(`/admin/auctions/${editing.id}`, payload);
      else await api.post("/admin/auctions", payload);
      toast.success("Leilão salvo");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const showBids = async (a) => {
    try {
      const r = await api.get(`/admin/auctions/${a.id}/bids`);
      setBidsFor({ auction: a, bids: r.data });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const finalize = async (a) => {
    if (!window.confirm("Encerrar agora e apurar o vencedor?")) return;
    try {
      await api.post(`/admin/auctions/${a.id}/finalize`);
      toast.success("Leilão encerrado e vencedor apurado");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (a) => {
    if (!window.confirm("Excluir este leilão?")) return;
    try {
      await api.delete(`/admin/auctions/${a.id}`);
      toast.success("Leilão excluído");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const selectedProduct = products.find((p) => p.id === editing?.product_id);

  return (
    <div data-testid="admin-auctions">
      <div className="flex items-center justify-between mb-10">
        <h1 className="font-display text-3xl tracking-tight">Leilões</h1>
        <Button onClick={() => setEditing({ ...BLANK })} className="bg-gold text-black hover:bg-gold-light" data-testid="new-auction-button">
          <Plus size={15} className="mr-1.5" /> Novo leilão
        </Button>
      </div>

      {items.length === 0 && <p className="text-sm text-zinc-500" data-testid="auctions-empty">Nenhum leilão criado ainda.</p>}

      <div className="space-y-4" data-testid="auctions-list">
        {items.map((a) => {
          const [label, cls] = STATUS[a.status] || STATUS.scheduled;
          return (
            <div key={a.id} className="border border-white/10 rounded-lg p-5 bg-[#111111]" data-testid={`auction-row-${a.id}`}>
              <div className="flex flex-wrap items-center gap-4 justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <p className="text-sm text-zinc-100">{a.product_name}</p>
                    <span className={`text-[11px] border rounded-full px-2.5 py-0.5 ${cls}`} data-testid={`auction-status-${a.id}`}>{label}</span>
                  </div>
                  <p className="text-xs text-zinc-500 mt-1.5">{fmtDt(a.start_at)} → {fmtDt(a.end_at)}</p>
                  <p className="text-xs text-zinc-500 mt-1">
                    Início em <span className="text-zinc-300">{money(a.starting_price)}</span> · incremento <span className="text-zinc-300">{money(a.min_increment)}</span>
                    {" · "}{a.bids_count || 0} lance(s)
                    {a.current_bid && <> · atual <span className="text-gold">{money(a.current_bid.amount)}</span></>}
                  </p>
                  {a.status === "ended" && (
                    a.winner ? (
                      <p className="text-xs mt-2 flex items-center gap-2 text-gold" data-testid={`auction-winner-${a.id}`}>
                        <Trophy size={13} /> Vencedor: {a.winner.name} ({a.winner.email}) — {money(a.winner.amount)} · entre em contato para cobrança
                      </p>
                    ) : (
                      <p className="text-xs text-zinc-600 mt-2" data-testid={`auction-winner-${a.id}`}>Sem lances</p>
                    )
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" variant="outline" className="border-white/20 text-zinc-200" onClick={() => showBids(a)} data-testid={`auction-bids-${a.id}`}>
                    <ListOrdered size={13} className="mr-1" /> Lances
                  </Button>
                  {a.status !== "ended" && (
                    <>
                      <Button size="sm" variant="outline" className="border-white/20 text-zinc-200" onClick={() => openEdit(a)} data-testid={`auction-edit-${a.id}`}>
                        <Pencil size={13} />
                      </Button>
                      <Button size="sm" variant="outline" className="border-white/20 text-zinc-200 hover:text-gold" onClick={() => finalize(a)} data-testid={`auction-finalize-${a.id}`}>
                        <OctagonX size={13} />
                      </Button>
                    </>
                  )}
                  {!a.bids_count && (
                    <Button size="sm" variant="ghost" className="text-zinc-500 hover:text-red-400" onClick={() => remove(a)} data-testid={`auction-delete-${a.id}`}>
                      <Trash2 size={13} />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#111111] border-white/10" data-testid="auction-dialog">
          <DialogHeader><DialogTitle className="font-display">{editing?.id ? "Editar leilão" : "Novo leilão"}</DialogTitle></DialogHeader>
          {editing && (
            <form onSubmit={save} className="space-y-4">
              <div>
                <Label>Produto *</Label>
                <Select value={editing.product_id} onValueChange={(v) => setEditing({ ...editing, product_id: v, variation_id: "" })} disabled={!!editing.id}>
                  <SelectTrigger className="mt-1.5 bg-[#141414] border-white/10" data-testid="auction-product-select"><SelectValue placeholder="Selecione o produto" /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-white/10">
                    {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {selectedProduct?.variations?.length > 0 && (
                <div>
                  <Label>Variação</Label>
                  <Select value={editing.variation_id} onValueChange={(v) => setEditing({ ...editing, variation_id: v })}>
                    <SelectTrigger className="mt-1.5 bg-[#141414] border-white/10" data-testid="auction-variation-select"><SelectValue placeholder="Primeira variação (padrão)" /></SelectTrigger>
                    <SelectContent className="bg-[#141414] border-white/10">
                      {selectedProduct.variations.map((v) => <SelectItem key={v.id} value={v.id}>{v.name} ({v.sku})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Início *</Label>
                  <Input type="datetime-local" required value={editing.start_at} onChange={(e) => setEditing({ ...editing, start_at: e.target.value })}
                    data-testid="auction-start-input" className="mt-1.5 bg-[#141414] border-white/10" />
                </div>
                <div>
                  <Label>Fim *</Label>
                  <Input type="datetime-local" required value={editing.end_at} onChange={(e) => setEditing({ ...editing, end_at: e.target.value })}
                    data-testid="auction-end-input" className="mt-1.5 bg-[#141414] border-white/10" />
                </div>
                <div>
                  <Label>Lance inicial (R$) *</Label>
                  <Input required value={editing.starting_price} onChange={(e) => setEditing({ ...editing, starting_price: e.target.value })}
                    placeholder="0,00" inputMode="decimal" data-testid="auction-startprice-input" className="mt-1.5 bg-[#141414] border-white/10" />
                </div>
                <div>
                  <Label>Incremento mínimo (R$) *</Label>
                  <Input required value={editing.min_increment} onChange={(e) => setEditing({ ...editing, min_increment: e.target.value })}
                    placeholder="0,00" inputMode="decimal" data-testid="auction-increment-input" className="mt-1.5 bg-[#141414] border-white/10" />
                </div>
              </div>
              <p className="text-[11px] text-zinc-600">Anti-snipe automático: lances nos últimos 5 minutos estendem o leilão em +5 minutos.</p>
              <Button type="submit" disabled={saving} className="w-full bg-gold text-black hover:bg-gold-light" data-testid="save-auction-button">
                {saving ? "Salvando..." : "Salvar"}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!bidsFor} onOpenChange={(o) => !o && setBidsFor(null)}>
        <DialogContent className="bg-[#111111] border-white/10" data-testid="bids-dialog">
          <DialogHeader><DialogTitle className="font-display">Lances — {bidsFor?.auction?.product_name}</DialogTitle></DialogHeader>
          {bidsFor && (
            bidsFor.bids.length === 0 ? (
              <p className="text-sm text-zinc-500">Nenhum lance ainda.</p>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {bidsFor.bids.map((b) => (
                  <div key={b.id} className="flex items-center justify-between gap-3 text-sm border-b border-white/5 pb-2" data-testid={`bid-admin-row-${b.id}`}>
                    <div className="min-w-0">
                      <p className="text-zinc-200 truncate">{b.user_name}</p>
                      <p className="text-[11px] text-zinc-600 truncate">{b.user_email} · {fmtDt(b.created_at)}</p>
                    </div>
                    <span className="text-gold shrink-0">{money(b.amount)}</span>
                  </div>
                ))}
              </div>
            )
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
